import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, TextField } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import format from 'string-template';

import { LinkButton } from '../../../../../../../components/Button';
import {
  PickerSearchRow,
  PickerList,
  PickerListHeaderRow,
  PickerListHeader,
  PickerRow,
  PickerRowName,
  PickerRowMeta,
  PickerShowMore,
  PickerHelperText,
  PickerErrorRow,
} from './styles';
import texts from './texts.json';

const LIST_CAP_THRESHOLD = 15;

const IngredientSearchTab = ({
  value,
  onChange,
  ingredients,
  isLoading,
  loadError,
  onRetryLoad,
}) => {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const selectedIds = useMemo(() => new Set(value.map((e) => e.id)), [value]);
  const q = query.trim().toLowerCase();

  const matches = useMemo(() => (
    q ? ingredients.filter((i) => i.name.toLowerCase().includes(q)) : ingredients
  ), [q, ingredients]);

  const shown = showAll ? matches : matches.slice(0, LIST_CAP_THRESHOLD);
  const hiddenCount = matches.length - shown.length;

  const allMatchesSelected = matches.length > 0 && matches.every((i) => selectedIds.has(i.id));
  const showSelectDeselect = matches.length > 0 && matches.length <= LIST_CAP_THRESHOLD;

  const handleQueryChange = useCallback((e) => {
    setQuery(e.target.value);
    setShowAll(false);
  }, []);

  const handleToggle = useCallback((ingredient) => {
    if (selectedIds.has(ingredient.id)) {
      onChange(value.filter((e) => e.id !== ingredient.id));
    } else {
      onChange([...value, { id: ingredient.id, name: ingredient.name }]);
    }
  }, [selectedIds, value, onChange]);

  const handleSelectAll = useCallback(() => {
    if (allMatchesSelected) {
      const matchIds = new Set(matches.map((i) => i.id));
      onChange(value.filter((e) => !matchIds.has(e.id)));
    } else {
      const toAdd = matches
        .filter((i) => !selectedIds.has(i.id))
        .map((i) => ({ id: i.id, name: i.name }));
      onChange([...value, ...toAdd]);
    }
  }, [allMatchesSelected, matches, selectedIds, value, onChange]);

  const listHeader = q
    ? format(texts.listHeaderFiltered, { count: matches.length })
    : format(texts.listHeaderAll, { count: ingredients.length });

  return (
    <>
      <PickerSearchRow>
        <SearchIcon fontSize="small" />
        <TextField
          variant="outlined"
          fullWidth
          value={query}
          onChange={handleQueryChange}
          placeholder={texts.searchPlaceholder}
        />
      </PickerSearchRow>

      <PickerList>
        {isLoading && (
          <PickerHelperText>{texts.loadingCatalog}</PickerHelperText>
        )}
        {!!loadError && (
          <PickerErrorRow>
            <span>{texts.loadError}</span>
            <LinkButton onClick={onRetryLoad}>{texts.loadRetry}</LinkButton>
          </PickerErrorRow>
        )}
        {!isLoading && !loadError && (
          <>
            <PickerListHeaderRow>
              <PickerListHeader>{listHeader}</PickerListHeader>
              {showSelectDeselect && (
                <PickerShowMore onClick={handleSelectAll}>
                  {allMatchesSelected ? texts.deselectAll : texts.selectAll}
                </PickerShowMore>
              )}
            </PickerListHeaderRow>
            {shown.length === 0 && (
              <PickerHelperText>{texts.noMatches}</PickerHelperText>
            )}
            {shown.map((ingredient) => (
              <PickerRow
                key={ingredient.id}
                onClick={() => handleToggle(ingredient)}
              >
                <Checkbox
                  checked={selectedIds.has(ingredient.id)}
                  onChange={() => handleToggle(ingredient)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
                <PickerRowName>{ingredient.name}</PickerRowName>
                {!!ingredient.category && (
                  <PickerRowMeta>{ingredient.category}</PickerRowMeta>
                )}
              </PickerRow>
            ))}
            {hiddenCount > 0 && (
              <PickerShowMore onClick={() => setShowAll(true)}>
                {format(texts.showMore, { count: hiddenCount })}
              </PickerShowMore>
            )}
          </>
        )}
      </PickerList>
    </>
  );
};

IngredientSearchTab.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  ingredients: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    category: PropTypes.string,
  })).isRequired,
  isLoading: PropTypes.bool,
  loadError: PropTypes.bool,
  onRetryLoad: PropTypes.func,
};

IngredientSearchTab.defaultProps = {
  isLoading: false,
  loadError: false,
  onRetryLoad: () => {},
};

export default IngredientSearchTab;
